import React, { Suspense } from 'react';
import { lazyWithRetry as lazy } from '@/utils/lazyWithRetry';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ErrorBoundaryWrapper } from './ErrorBoundaryWrapper';
import { MobileLayoutWrapper } from './MobileLayoutWrapper';

// Lazy load pages for better performance
export const LazyIndex = lazy(() => import('../../pages/Index'));
export const LazyCoinShop = lazy(() => import('../../pages/CoinShop'));
export const LazyEarn = lazy(() => import('../../pages/Earn'));
export const LazyReportReader = lazy(() => import('../../pages/ReportReader'));
export const LazyReports = lazy(() => import('../../pages/Reports'));
export const LazyPsychosophy = lazy(() => import('../../pages/Psychosophy'));
export const LazyPoliticalCompass = lazy(() => import('../../pages/PoliticalCompass'));
export const LazySkillTree = lazy(() => import('../../pages/SkillTree'));
export const LazyCognitiveReport = lazy(() => import('../../pages/CognitiveReport'));
export const LazyIQTest = lazy(() => import('../../pages/IQTest'));
export const LazyIQShared = lazy(() => import('../../pages/IQShared'));
export const LazyAuth = lazy(() => import('../../pages/Auth'));
export const LazyResetPassword = lazy(() => import('../../pages/ResetPassword'));
export const LazyAdmin = lazy(() => import('../../pages/Admin'));
export const LazyAssessmentHistory = lazy(() => import('../../pages/AssessmentHistory'));
export const LazyAssessments = lazy(() => import('../../pages/Assessments'));
export const LazyProfile = lazy(() => import('../../pages/Profile'));
export const LazyMentor = lazy(() => import('../../pages/Mentor'));
export const LazyMentorBoard = lazy(() => import('../../pages/MentorBoard'));
export const LazyMentorArc = lazy(() => import('../../pages/MentorArc'));
export const LazyNotFound = lazy(() => import('../../pages/NotFound'));
export const LazyIntegralLanding = lazy(() => import('../../pages/IntegralLanding'));
export const LazyIntegralHub = lazy(() => import('../../pages/Integral'));
export const LazyAssessmentSelection = lazy(() => import('../../components/assessment/AssessmentSelection'));
export const LazyTPSProfile = lazy(() => import('../../pages/TPSProfile'));

// Astrology (opt-in)
export const LazyAstrology = lazy(() => import('../../pages/Astrology'));

// Personality Science (public articles)
export const LazyScience = lazy(() => import('../../pages/Science'));
export const LazyScienceArticle = lazy(() => import('../../pages/ScienceArticle'));

// TPS Domain Assessment pages
export const LazyTPSOnboarding = lazy(() => import('../../pages/TPSOnboarding'));
export const LazyTPSAssessmentHub = lazy(() => import('../../pages/TPSAssessmentHub'));
export const LazyTPSDomainAssessment = lazy(() => import('../../components/assessment/TPSDomainAssessment'));
export const LazyTPSDomainResults = lazy(() => import('../../pages/TPSDomainResults'));
export const LazyTPSFinalSynthesis = lazy(() => import('../../pages/TPSFinalSynthesis'));

// Liker pages
export const LazyLiker = lazy(() => import('../../pages/Liker'));
export const LazyLikerSession = lazy(() => import('../../pages/LikerSession'));
export const LazyLikerCollections = lazy(() => import('../../pages/LikerCollections'));
export const LazyLikerCollectionDetail = lazy(() => import('../../pages/LikerCollectionDetail'));

// Additional pages (consolidated from App.tsx ad-hoc lazy() calls)
export const LazyIntegralAssessment = lazy(() => import('../../pages/IntegralAssessment'));
export const LazyConnections = lazy(() => import('../../pages/Connections'));
export const LazyLeaderboard = lazy(() => import('../../pages/Leaderboard'));
export const LazyTestSessions = lazy(() => import('../../pages/TestSessions'));
export const LazyPublicProfile = lazy(() => import('../../pages/PublicProfile'));
export const LazyCommons = lazy(() => import('../../pages/Commons'));
export const LazyMail = lazy(() => import('../../pages/Mail'));
export const LazyCommunity = lazy(() => import('../../pages/Community'));
export const LazyJoinInvite = lazy(() => import('../../pages/JoinInvite'));
export const LazyExplore = lazy(() => import('../../pages/Explore'));
export const LazyNotifications = lazy(() => import('../../pages/Notifications'));
export const LazyFeed = lazy(() => import('../../pages/Feed'));
export const LazyBookmarks = lazy(() => import('../../pages/Bookmarks'));
export const LazyCards = lazy(() => import('../../pages/Cards'));
export const LazyCharacterQuiz = lazy(() => import('../../pages/CharacterQuiz'));
export const LazyQuizDirectory = lazy(() => import('../../pages/QuizDirectory'));
export const LazyQuizRouter = lazy(() => import('../../pages/QuizRouter'));
export const LazyDemoPersonalityResults = lazy(() => import('../../pages/demo/DemoPersonalityResults'));
export const LazyDemoIntegralResults = lazy(() => import('../../pages/demo/DemoIntegralResults'));
export const LazyDemoMentor = lazy(() => import('../../pages/demo/DemoMentor'));
export const LazyDemoTPSProfile = lazy(() => import('../../pages/demo/DemoTPSProfile'));
export const LazyDemoHub = lazy(() => import('../../pages/demo/DemoHub'));
export const LazyInsights = lazy(() => import('../../pages/Insights'));
export const LazyUnifiedPersonality = lazy(() => import('../../pages/UnifiedPersonality'));
export const LazyGuestResultsPreview = lazy(() => import('../../pages/GuestResultsPreview'));
export const LazyDisambiguationQuizSession = lazy(() => import('../../pages/DisambiguationQuizSession'));
export const LazyFrameworkClarify = lazy(() => import('../../pages/FrameworkClarify'));
export const LazySharedResultsView = lazy(() => import('../../pages/SharedResultsView'));
export const LazyTestDirectory = lazy(() => import('../../pages/TestDirectory'));
export const LazyCareer = lazy(() => import('../../pages/Career'));
export const LazyRelationships = lazy(() => import('../../pages/Relationships'));
export const LazyRelationshipsHub = lazy(() => import('../../pages/RelationshipsHub'));
export const LazyRelationshipDetail = lazy(() => import('../../pages/RelationshipDetail'));
export const LazyRelationshipGroups = lazy(() => import('../../pages/RelationshipGroups'));
export const LazyRelationshipMessages = lazy(() => import('../../pages/RelationshipMessages'));
export const LazyAgentSessions = lazy(() => import('../../pages/AgentSessions'));
export const LazyRelationshipInvite = lazy(() => import('../../pages/RelationshipInvite'));
export const LazySubscription = lazy(() => import('../../pages/Subscription'));
export const LazyEnterprise = lazy(() => import('../../pages/Enterprise'));
export const LazyCompatibilitySearch = lazy(() => import('../../pages/CompatibilitySearch'));
export const LazySocial = lazy(() => import('../../pages/Social'));
export const LazyMbtiLevelsMatrix = lazy(() => import('../../pages/admin/MbtiLevelsMatrix'));
export const LazyResonanceAuditPanel = lazy(() => import('../../pages/admin/ResonanceAuditPanel'));
export const LazyAtlas = lazy(() => import('../../pages/admin/Atlas'));
export const LazyDocs = lazy(() => import('../../pages/admin/Docs'));
export const LazyCoinRequests = lazy(() => import('../../pages/admin/CoinRequests'));
export const LazyModeration = lazy(() => import('../../pages/admin/Moderation'));
export const LazyBroadcasts = lazy(() => import('../../pages/admin/Broadcasts'));
export const LazyUnsubscribe = lazy(() => import('../../pages/Unsubscribe'));
export const LazySignalHealth = lazy(() => import('../../pages/admin/SignalHealth'));
export const LazyLikerCatalog = lazy(() => import('../../pages/admin/LikerCatalog'));
export const LazyFrameworkDetail = lazy(() => import('../../pages/FrameworkDetail'));
export const LazyBrandPreview = lazy(() => import('../../pages/dev/BrandPreview'));
export const LazyMotionLab = lazy(() => import('../../pages/dev/MotionLab'));
export const LazyDailyReflection = lazy(() => import('../../pages/DailyReflection'));

// Page wrapper with loading fallback, error boundaries, and mobile optimization
// Each page controls its own navigation layout
export const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundaryWrapper>
      <MobileLayoutWrapper>
        <Suspense fallback={
          <div className="min-h-dvh bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
          {children}
        </Suspense>
      </MobileLayoutWrapper>
    </ErrorBoundaryWrapper>
  );
};
